import { Button, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { Address } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { formatAddress } from '@pelicargo/utils';
import { useFormContext } from 'react-hook-form';
import { HiPencilSquare, HiUserPlus } from 'react-icons/hi2';

import { useSelectedContact } from '../hooks/contact/useSelectedContact';
import { ContactInput } from './Autocomplete/ContactInput';
import { ContactFormDrawer } from './Drawers/ContactFormDrawer';
import { LabeledText } from './LabeledText';
import { useSimpleFormContext } from './SimpleForm';

type Props = {
  /** RHF path storing the selected contact id (e.g. `shipper_contact_id`). */
  name: string;
  label: string;
  defaultAccountIds?: string[];
  defaultAirlineIds?: string[];
  defaultAirportIds?: string[];
};

export const ContactSelectInput = ({ name, label, defaultAccountIds, defaultAirlineIds, defaultAirportIds }: Props) => {
  const createDrawer = useDisclosure();
  const editDrawer = useDisclosure();
  const { setValue } = useFormContext();
  const { updateField } = useSimpleFormContext();

  const { contact } = useSelectedContact(name);

  const handleContactCreate = (created: { id?: number } | null) => {
    if (!created?.id) return;
    setValue(name, created.id, { shouldDirty: true });
    void updateField?.(name, created.id);
    createDrawer.onClose();
  };

  const address = contact?.address ? formatAddress(contact.address as Address) : '';

  return (
    <Stack spacing={1}>
      <ContactInput
        name={name}
        label={label}
        defaultAccountIds={defaultAccountIds}
        defaultAirlineIds={defaultAirlineIds}
        defaultAirportIds={defaultAirportIds}
      />
      <Show if={!!contact}>
        <Stack px={2} fontSize="xs">
          <LabeledText
            label="Name"
            value={contact?.full_name || contact?.preferred_name || 'No name on file'}
            isHorizontal
            isCopyable
          />
          <LabeledText label="Email" value={contact?.email || 'No email'} isHorizontal isCopyable />
          <Show if={!!contact?.phone_number}>
            <LabeledText label="Phone" value={contact?.phone_number} isHorizontal isCopyable />
          </Show>
          <LabeledText label="Address" value={address || 'No address on file'} isHorizontal isCopyable />
          <Show if={!!contact?.notes}>
            <LabeledText label="Notes" value={contact?.notes} isHorizontal isCopyable title={contact?.notes} />
          </Show>
        </Stack>
      </Show>
      <HStack spacing={1}>
        <Show if={!!contact}>
          <Button
            size="xs"
            variant="ghost"
            colorScheme="blue"
            leftIcon={<HiPencilSquare />}
            onClick={editDrawer.onOpen}
          >
            Edit contact
          </Button>
        </Show>
        <Button size="xs" variant="ghost" colorScheme="blue" leftIcon={<HiUserPlus />} onClick={createDrawer.onOpen}>
          Create contact
        </Button>
      </HStack>
      <ContactFormDrawer
        isOpen={createDrawer.isOpen}
        onClose={createDrawer.onClose}
        onContactCreate={handleContactCreate}
      />
      <Show if={!!contact}>
        <ContactFormDrawer contactId={contact?.id} isOpen={editDrawer.isOpen} onClose={editDrawer.onClose} />
      </Show>
    </Stack>
  );
};
